import { WebglObject } from "../../../../../web-graphics";
import { HistogramSignalBuffer } from "./histogram-signal-buffer";

export class HistogramSignal extends WebglObject {
  constructor(settings) {
    super(settings);
    this.settings.ppmm = this.settings.ppcm / 10; // pixels per mm
    const gl = this.settings.gl;

    this.isBufferInitMap = new Map();
    this.isBufferInitMap.set(gl.ARRAY_BUFFER, false);
    this.isBufferInitMap.set(gl.ELEMENT_ARRAY_BUFFER, false);

    this.filters = new Map();
    for (let filterIndex = 0; filterIndex < settings.filters.length; filterIndex++) {
      const filterInfo = settings.filters[filterIndex];
      this.filters.set(filterInfo.name, {
        index: filterIndex,
        filter: filterInfo.filter
      });
    }

    const signalBufferSettings = HistogramSignal.createSignalBufferSettings(this.settings, this.filters);
    this.signalBuffer = new HistogramSignalBuffer(signalBufferSettings);
    this.lastPoints = null;

    const parentSettings = this.settings.parent.ref.settings;
    this.uniforms = {
      u_color: new Float32Array(this.settings.color),
      u_shift: new Float32Array([0.0, this.settings.relatedLocation.y]),
      u_rollIndex: 0,
      u_scale: new Float32Array([1, 1]),
      u_windowSize: new Float32Array([parentSettings.clientSize.width, parentSettings.clientSize.height])
    };

    this.vertexGlBuffer = gl.createBuffer();
    this.indexGlBuffer = gl.createBuffer();

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexGlBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.signalBuffer.vertexBuffer, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexGlBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.signalBuffer.indexBuffer, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
  }

  static createSignalBufferSettings(settings, filters) {
    const selectedFilters = HistogramSignal.getSelectedFilters(settings.selectedFiltersNames, filters);
    return {
      pointCounterCreator: settings.pointCounterCreator,
      ppmm: settings.ppmm,
      plotSize: settings.parent.ref.settings.clientSize,
      windowSize: settings.parent.ref.settings.clientSize,
      freq: settings.freq,
      scaleX: settings.scaleX * settings.viewScale.x,
      scaleY: settings.scaleY * settings.viewScale.y,
      filters: selectedFilters,
      valuePixelConvertKoef: settings.valuePixelConvertKoef,
      isolineBorder: settings.isolineBorder,
      isDataSync: !settings.isBuffered,
      dataBufferSize: settings.dataBufferSize,
      pointCount: settings.pointCount
    };
  }

  static getSelectedFilters(selectedFiltersNames, filters) {
    let selectedFilters = [];
    selectedFilters.length = selectedFiltersNames.length;
    selectedFilters.fill({});
    for (let i = 0; i < selectedFiltersNames.length; i++) {
      selectedFilters[i] = filters.get(selectedFiltersNames[i]);
    }
    selectedFilters.sort((a, b) => a.index - b.index);
    return selectedFilters;
  }

  tryUpdateMeanSignalValue() {}

  addPoints(points) {
    this.dataIsInitialized = true;
    let commonDataWidth = 0;
    let startIndex = this.signalBuffer.currColumnIndex;
    let updateRanges = [];
    for (let dataIndex = 0; dataIndex < points.length; dataIndex++) {
      this.signalBuffer.addData(points[dataIndex]);
      commonDataWidth += points[dataIndex].width;
      if (this.signalBuffer.currColumnIndex < startIndex) {
        updateRanges.push({
          begin: startIndex,
          end: this.signalBuffer.columnCount - 1
        });
        startIndex = 0;
      }
    }
    updateRanges.push({
      begin: startIndex,
      end: this.signalBuffer.currColumnIndex
    });

    if (commonDataWidth >= this.settings.parent.ref.settings.clientSize.width) {
      this.resetWebglBuffers();
    } else {
      for (let rangeIndex = 0; rangeIndex < updateRanges.length; rangeIndex++) {
        this.updateWebglBuffers(updateRanges[rangeIndex].begin, updateRanges[rangeIndex].end);
      }
    }

    if (this.lastPoints !== points){
      this.lastPoints = points;
    }
  }

  updateDataBuffer() {
    const parentSettings = this.settings.parent.ref.settings;
    this.uniforms.u_shift[1] = this.settings.relatedLocation.y;
    this.uniforms.u_windowSize[0] = parentSettings.clientSize.width;
    this.uniforms.u_windowSize[1] = parentSettings.clientSize.height;
    this.signalBuffer.updateWindowSize(parentSettings.clientSize, parentSettings.clientSize);
    this.resetWebglBuffers();
  }

  createBorders() {
    super.createBorders();
    this.updateDataBuffer();
  }

  resetWebglBuffers() {
    const gl = this.settings.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexGlBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.signalBuffer.vertexBuffer, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexGlBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.signalBuffer.indexBuffer, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
  }

  updateWebglBuffers(beginColumnIndex, endColumnIndex) {
    const gl = this.settings.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexGlBuffer);
    let updatedBuffer = this.signalBuffer.vertexBuffer.subarray(beginColumnIndex * 8, endColumnIndex * 8);
    gl.bufferSubData(gl.ARRAY_BUFFER, beginColumnIndex * 8 * 4, updatedBuffer); // index * vertexPerRect * bytesForVertex
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexGlBuffer);
    updatedBuffer = this.signalBuffer.indexBuffer.subarray(beginColumnIndex * 6, endColumnIndex * 6);
    gl.bufferSubData(gl.ELEMENT_ARRAY_BUFFER, beginColumnIndex * 6 * 2, updatedBuffer); // index * vertexPerRect * bytesForVertex
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
  }

  setDataToGraphicBuffer(bufferType, startIndex, dataBuffer) {
    const gl = this.settings.gl;
    if (this.signalBuffer.isFullWidth) {
      if (this.isBufferInitMap.get(bufferType)) {
        gl.bufferSubData(bufferType, startIndex, dataBuffer);
      } else {
        gl.bufferData(bufferType, dataBuffer.length * dataBuffer.BYTES_PER_ELEMENT, gl.DYNAMIC_DRAW);
        this.isBufferInitMap.set(bufferType, true);
      }
    } else {
      gl.bufferData(bufferType, dataBuffer.length * dataBuffer.BYTES_PER_ELEMENT, gl.DYNAMIC_DRAW);
    }
  }

  resetBuffer() {
    this.signalBuffer.reset();
    this.resetWebglBuffers();
  }

  setFilters(selectedFiltersNames) {
    this.signalBuffer.setFilters(HistogramSignal.getSelectedFilters(selectedFiltersNames, this.filters));
    this.resetWebglBuffers();
  }

  getLastValidGraphicDataIndex() {
    return this.signalBuffer.getLastValidColumnIndex();
  }

  getLastValidPointX() {
    return this.signalBuffer.getLastValidPointX();
  }

  getData(columnIndex) {
    return this.signalBuffer.getDataByIndex(columnIndex);
  }

  getBufferData(x) {
    return this.signalBuffer.getData(x);
  }

  getFirstBufferIndex(x) {
    return this.signalBuffer.getColumnIndex(x);
  }

  getLastBufferIndex(x) {
    return this.signalBuffer.getColumnIndex(x);
  }

  setScaleY(newScaleY){
    if (this.lastPoints){
      this.signalBuffer.setScaleY(newScaleY);
      this.addPoints(this.lastPoints);
    }

  }

  draw(gl, program) {
    if (!this.dataIsInitialized || this.signalBuffer.columnCount == 0) {
      return;
    }
    const parentSettings = this.settings.parent.ref.settings;
    gl.viewport(
      parentSettings.absoluteLocation.x,
      parentSettings.absoluteLocation.y,
      parentSettings.clientSize.width,
      parentSettings.clientSize.height
    );
    program.setUnforms(this.uniforms);

    gl.enableVertexAttribArray(program.attribs.a_position);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexGlBuffer);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexGlBuffer);
    gl.vertexAttribPointer(program.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
    gl.drawElements(gl.TRIANGLES, this.signalBuffer.columnCount * 6, gl.UNSIGNED_SHORT, 0);
  }
}
